import { useEffect } from 'react'
import { hasWindow, getWindow } from '@ztechapps/common.utils'
import { useTelemetry } from './useTelemetry'

/**
 * Web Vitals metric names
 */
const WEB_VITALS_METRICS = {
  CLS: 'web_vitals.cls', // Cumulative Layout Shift
  FCP: 'web_vitals.fcp', // First Contentful Paint
  FID: 'web_vitals.fid', // First Input Delay (deprecated, replaced by INP)
  INP: 'web_vitals.inp', // Interaction to Next Paint
  LCP: 'web_vitals.lcp', // Largest Contentful Paint
  TTFB: 'web_vitals.ttfb', // Time to First Byte
} as const

/**
 * Hook to collect and report Core Web Vitals metrics
 *
 * Collects LCP, FID, CLS, FCP, TTFB, and INP metrics and reports
 * them to OpenTelemetry. Should be called once in the root component.
 *
 * @example
 * ```tsx
 * function App() {
 *   useWebVitals() // Call once at app root
 *
 *   return (
 *     <TelemetryProvider config={config}>
 *       <Routes>...</Routes>
 *     </TelemetryProvider>
 *   )
 * }
 * ```
 */
export function useWebVitals() {
  const telemetry = useTelemetry()

  useEffect(() => {
    if (!telemetry || !hasWindow()) return

    // Dynamically import web-vitals to avoid SSR issues
    import('web-vitals').then(({ onCLS, onFCP, onFID, onINP, onLCP, onTTFB }) => {
      const reportMetric = (name: string, value: number, rating: string) => {
        telemetry.recordHistogram(name, value, {
          rating, // 'good', 'needs-improvement', or 'poor'
          page_path: getWindow().location.pathname,
        })
      }

      // Cumulative Layout Shift
      onCLS((metric) => {
        reportMetric(WEB_VITALS_METRICS.CLS, metric.value, metric.rating)
      })

      // First Contentful Paint
      onFCP((metric) => {
        reportMetric(WEB_VITALS_METRICS.FCP, metric.value, metric.rating)
      })

      // First Input Delay (legacy, for older browsers)
      onFID((metric) => {
        reportMetric(WEB_VITALS_METRICS.FID, metric.value, metric.rating)
      })

      // Interaction to Next Paint (new responsiveness metric)
      onINP((metric) => {
        reportMetric(WEB_VITALS_METRICS.INP, metric.value, metric.rating)
      })

      // Largest Contentful Paint
      onLCP((metric) => {
        reportMetric(WEB_VITALS_METRICS.LCP, metric.value, metric.rating)
      })

      // Time to First Byte
      onTTFB((metric) => {
        reportMetric(WEB_VITALS_METRICS.TTFB, metric.value, metric.rating)
      })
    })
  }, [telemetry])
}

/**
 * Hook to measure custom performance timing
 *
 * @param measurementName - Name for the performance measurement
 * @returns Object with start/end functions
 *
 * @example
 * ```tsx
 * function DataLoader() {
 *   const timing = usePerformanceTiming('data_load')
 *
 *   useEffect(() => {
 *     timing.start()
 *     fetchData().finally(() => {
 *       timing.end()
 *     })
 *   }, [])
 * }
 * ```
 */
export function usePerformanceTiming(measurementName: string) {
  const telemetry = useTelemetry()
  let startTime: number | null = null

  return {
    start: () => {
      startTime = performance.now()
    },
    end: (attributes?: Record<string, string>) => {
      if (startTime !== null) {
        const duration = performance.now() - startTime
        telemetry?.recordHistogram(`timing.${measurementName}`, duration, {
          unit: 'ms',
          ...attributes,
        })
        startTime = null
      }
    },
  }
}
